import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Download, Users, X, Search } from 'lucide-react';
import { posApi } from '@/services/api';
import { formatMoney } from '@/utils/currency';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import { useReportDateRange } from '@/hooks/useReportDateRange';

interface LedgerEntry {
  posting_date: string;
  invoice_no: string;
  payment_no: string;
  description: string;
  items_string?: string;
  debit: number;
  credit: number;
  balance: number;
}

interface LedgerResponse {
  party_name: string;
  opening_balance: number;
  closing_balance: number;
  balance_label: string;
  entries: LedgerEntry[];
  from_date: string;
  to_date: string;
}

interface SaleRegisterCustomer {
  name: string;
  customer_name: string;
  mobile?: string;
}

const formatDate = (d?: string) => {
  if (!d) return '';
  try {
    return format(new Date(d), 'd-MMM-yyyy');
  } catch {
    return d;
  }
};

export default function CustomerLedgerReportSection() {
  const { t } = useTranslation();
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedCustomerLabel, setSelectedCustomerLabel] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const customerDropdownRef = useRef<HTMLDivElement>(null);

  const { data: customersRes } = useQuery({
    queryKey: ['customersForLedger', customerSearch],
    queryFn: async () => {
      const res = await posApi.getCustomers({ search: customerSearch, limit: 50 });
      return res.data.message;
    },
  });

  const customers: SaleRegisterCustomer[] = customersRes?.customers || customersRes || [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target as Node)) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectCustomer = (customer?: SaleRegisterCustomer) => {
    if (!customer) {
      setSelectedCustomer('');
      setSelectedCustomerLabel('');
    } else {
      setSelectedCustomer(customer.name);
      setSelectedCustomerLabel(
        customer.mobile ? `${customer.customer_name} - ${customer.mobile}` : customer.customer_name
      );
    }
    setCustomerSearch('');
    setShowCustomerDropdown(false);
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['partyLedger', 'POS Customer', selectedCustomer, from_date, to_date],
    queryFn: async () => {
      const res = await posApi.getPartyLedger({
        party_type: 'POS Customer',
        party: selectedCustomer || '',
        from_date,
        to_date,
      });
      return (res.data.message || {}) as LedgerResponse;
    },
    staleTime: 0,
  });

  const downloadCSV = () => {
    if (!data) return;
    const lines = [
      'Date,Invoice #,Payment #,Description,Debit,Credit,Balance',
      `,,,Opening Balance,,,${data.opening_balance ?? 0}`,
      ...(data.entries || []).map((row) =>
        [
          formatDate(row.posting_date),
          row.invoice_no || '',
          row.payment_no || '',
          `"${(row.description || '').replace(/"/g, '""')}"`,
          row.debit || 0,
          row.credit || 0,
          row.balance ?? 0,
        ].join(',')
      ),
      `,,,Closing Balance,,,${data.closing_balance ?? 0}`,
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute(
      'download',
      `customer_ledger_${selectedCustomer}_${from_date}_${to_date}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const closing = data?.closing_balance ?? 0;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/60 border border-slate-100 overflow-hidden">
        <div className="p-5 border-b border-slate-100 space-y-4">
          <div className="flex flex-wrap gap-4 items-end">
            <ReportDateRangeFilter
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              customFrom={customFrom}
              customTo={customTo}
              onCustomFromChange={setCustomFrom}
              onCustomToChange={setCustomTo}
            />

            <div className="space-y-1.5 flex-1 min-w-[200px] sm:min-w-[240px] relative" ref={customerDropdownRef}>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-400" />
                {t('reports.customer')}
              </label>
              {selectedCustomer ? (
                <div className="input-field flex items-center justify-between gap-2 py-2 bg-gray-50 border border-gray-200 shadow-sm rounded-lg">
                  <span className="truncate text-sm text-gray-900 font-medium px-1">{selectedCustomerLabel}</span>
                  <button
                    type="button"
                    onClick={() => handleSelectCustomer()}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded"
                    aria-label="Clear customer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      className="input-field pl-9"
                      placeholder={t('reports.searchByNamePhone')}
                      value={customerSearch}
                      onChange={(e) => {
                        setCustomerSearch(e.target.value);
                        setShowCustomerDropdown(true);
                      }}
                      onFocus={() => setShowCustomerDropdown(true)}
                    />
                  </div>
                  {showCustomerDropdown && (
                    <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto">
                      {customers.length > 0 ? (
                        customers.map((c) => (
                          <button
                            key={c.name}
                            type="button"
                            onClick={() => handleSelectCustomer(c)}
                            className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50"
                          >
                            <p className="font-medium text-gray-900">{c.customer_name}</p>
                            {c.mobile && <p className="text-xs text-gray-500">{c.mobile}</p>}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-4 text-sm text-gray-400 text-center">{t('reports.noCustomersFound')}</div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <button
              type="button"
              onClick={downloadCSV}
              disabled={!data || data.entries.length === 0}
              className="btn-primary w-full sm:w-auto py-2.5 px-4 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {t('reports.exportCsv')}
            </button>
          </div>

          {data && (
            <div className="flex flex-wrap gap-3 text-sm">
              <div className="px-3 py-2 rounded-lg bg-white border border-gray-200">
                <span className="text-gray-500">{t('partyLedger.opening')} </span>
                <span className="font-semibold text-gray-900">{formatMoney(data.opening_balance)}</span>
              </div>
              <div className="px-3 py-2 rounded-lg bg-white border border-gray-200">
                <span className="text-gray-500">{t('partyLedger.closing')} </span>
                <span
                  className={`font-semibold ${
                    closing > 0 ? 'text-amber-700' : closing < 0 ? 'text-emerald-700' : 'text-gray-900'
                  }`}
                >
                  {formatMoney(closing)}
                </span>
                {data.balance_label && (
                  <span className="text-gray-500 ml-2">({data.balance_label})</span>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          {isLoading || (isFetching && !data) ? (
            <div className="p-8 flex justify-center items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : data?.entries?.length ? (
            <table className="w-full text-sm text-left min-w-[700px]">
              <thead className="text-xs font-bold text-[#1fa8ff] bg-gray-50 uppercase sticky top-0 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3">{t('common.date')}</th>
                  <th className="px-4 py-3">{t('reports.invoice')} #</th>
                  <th className="px-4 py-3">{t('partyLedger.paymentNo')}</th>
                  <th className="px-4 py-3 w-1/3">{t('common.description')}</th>
                  <th className="px-4 py-3 text-right">{t('partyLedger.debit')}</th>
                  <th className="px-4 py-3 text-right">{t('partyLedger.credit')}</th>
                  <th className="px-4 py-3 text-right">{t('reports.balance')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr className="bg-gray-50/50">
                  <td colSpan={6} className="px-4 py-3 text-right font-medium text-gray-600">
                    {t('partyLedger.opening')}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-gray-900">
                    {formatMoney(data.opening_balance)}
                  </td>
                </tr>
                {data.entries.map((row, i) => (
                  <tr key={i} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                      {formatDate(row.posting_date)}
                    </td>
                    <td className="px-4 py-3 text-primary-600 font-medium">
                      {row.invoice_no || '—'}
                    </td>
                    <td className="px-4 py-3 text-indigo-600">
                      {row.payment_no || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-700">{row.description}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">
                      {row.debit ? formatMoney(row.debit) : ''}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-emerald-600">
                      {row.credit ? formatMoney(row.credit) : ''}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-gray-900">
                      {formatMoney(row.balance)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-50">
                  <td colSpan={6} className="px-4 py-3 text-right font-medium text-gray-700">
                    {t('partyLedger.closing')}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-gray-900">
                    {formatMoney(data.closing_balance)}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <div className="p-8 text-center text-gray-500">
              {t('common.noData')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
